import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import './styles/index.css';
import { assertConfig } from './lib/config';
import { configureAuth } from './lib/auth/amplify';
import { AuthProvider } from './lib/auth/auth-context';
import { ErrorBoundary } from './components/error-boundary';
import { ToastProvider } from './components/ui/toast';
import { initI18n } from './lib/i18n';
import { router } from './router';
import { ApiError } from './lib/api/client';

// Before React: an invalid `config.js` must fail on the first paint rather than at
// the first request, and Amplify must be configured before any component asks for a
// session.
assertConfig();
configureAuth();
initI18n();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retrying a 401 or a 403 is pointless — the answer will not change
      // without a new session or new access — and retrying a ContractError
      // just repeats a failed parse. Everything else gets two attempts.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && (error.isUnauthenticated || error.isForbidden)) {
          return false;
        }

        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
    },
  },
});

const root = document.getElementById('root');

if (!root) {
  throw new Error('index.html is missing #root');
}

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          {/*
            Outside the router, not inside a screen: a toast is usually raised by
            a dialog closing, and half the time the thing that raised it is a
            navigation. Mounted here it outlives both.
          */}
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
